"use client";

import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  changeActiveQuestionExplanationTranslation,
  changeActiveQuestionOption1Translation,
  changeActiveQuestionOption2Translation,
  changeActiveQuestionOption3Translation,
  changeActiveQuestionOption4Translation,
  changeActiveQuestionTranslation,
} from "../../../store/slices/proofReading/questionDataArraySlice";
import mathFunction from "../../../pages/exams/mathFunction";

const AllQuestionPreview: React.FC<{ question: any }> = ({ question }) => {
  // const AllQuestionPreviewRef = useRef(null)
  const activeQuestionData = question;

  const correction = activeQuestionData?.corrections ?? null;

  const token = localStorage.getItem("npc-admin-token")
    ? JSON.parse(localStorage.getItem("npc-admin-token") || "")
    : null;
  const proofReadingPermissions = localStorage.getItem("npc-admin-permissions")
    ? JSON.parse(localStorage.getItem("npc-admin-permissions") || "")
    : null;

  // State for editing different parts of the question
  const [editingStates, setEditingStates] = useState<any>({
    question: correction && correction.question,
    explanation: correction && correction.explanation,
    option1: correction && correction.option1,
    option2: correction && correction.option2,
    option3: correction && correction.option3,
    option4: correction && correction.option4,
  });
  useEffect(() => {
    mathFunction();
    let timeOut: ReturnType<typeof setTimeout>;

    timeOut = setTimeout(() => mathFunction(), 500);

    return () => clearTimeout(timeOut);
  }, [question, editingStates, activeQuestionData]);
  // State for edited content
  const [editedContent, setEditedContent] = useState<any>({
    question: correction && correction.question ? correction.question : "",
    explanation:
      correction && correction.explanation ? correction.explanation : "",
    option1: correction && correction.option1 ? correction.option1 : "",
    option2: correction && correction.option2 ? correction.option2 : "",
    option3: correction && correction.option3 ? correction.option3 : "",
    option4: correction && correction.option4 ? correction.option4 : "",
  });

  useEffect(() => {
    const corrections = question?.corrections;
    setEditedContent({
      question: corrections && corrections.question ? corrections.question : "",
      explanation:
        corrections && corrections.explanation ? corrections.explanation : "",
      option1: corrections && corrections.option1 ? corrections.option1 : "",
      option2: corrections && corrections.option2 ? corrections.option2 : "",
      option3: corrections && corrections.option3 ? corrections.option3 : "",
      option4: corrections && corrections.option4 ? corrections.option4 : "",
    });
  }, [question]);

  const [modalInfo, setModalInfo] = useState<{
    rawText: string;
    visible: boolean;
    proofreadedcontent?: boolean;
  }>({
    rawText: "",
    visible: true,
    proofreadedcontent: true,
  });

  if (proofReadingPermissions === null)
    return <Navigate to={"/login-page"} replace />;
  if (!activeQuestionData) return null;

  // Helper function to render editable field
  const renderEditableField = (
    isEditing: boolean,
    fieldKey: any,
    originalValue: string,
    dispatchAction: any,
    coordinateField: string,
    coordinateDateField: string,
    editedDate?: string,
    label?: string,
    englishContent?: any
  ) => {
    // Get the current value from our editedContent state
    const currentValue = editedContent[fieldKey] || originalValue || "";
    return (
      <Stack spacing={"8px"} marginTop={"16px"} width="100%">
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          spacing={"16px"}
          width="100%"
          alignItems={"center"}
        >
          <Stack direction={"row"} alignItems={"center"}>
            <Typography
              variant="h6"
              fontSize="0.9rem"
              fontWeight={500}
              color="#374151"
              sx={{
                textDecoration: "underline",
                textUnderlineOffset: "4px",
              }}
            >
              {label ? label : fieldKey ?? ""}
            </Typography>
            {editedDate && (
              <Button
                sx={{ textTransform: "capitalize" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setModalInfo({
                    visible: true,
                    rawText: originalValue,
                  });
                }}
              >{`edited on ${editedDate}`}</Button>
            )}
          </Stack>
          {/* {proofReadingPermissions.includes(
            proofReadingPermission.change_traslation
          ) && isEditing ? (
            <IconButton
              aria-label="edit"
              sx={{ padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();

                toggleEditing(fieldKey, false);
              }}
            >
              <Cancel />
            </IconButton>
          ) : (
            <IconButton
              aria-label="edit"
              sx={{ padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();
                toggleEditing(fieldKey, true);
              }}
            >
              <Edit />
            </IconButton>
          )} */}
        </Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          spacing={"16px"}
          width="100%"
          alignItems={"center"}
        >
          {" "}
          <Box width="calc(50% - 8px)" height="100%" display="flex">
            <Typography
              variant="body2"
              sx={{
                bgcolor: "#f9fafb",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
                padding: "5px 10px",
                width: "100%",
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: englishContent,
                }}
              ></div>
            </Typography>
          </Box>
          <Box width="calc(50% - 8px)" height="100%" display="flex">
            {
              <Typography
                variant="body2"
                sx={{
                  bgcolor: "#f9fafb",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  padding: "5px 10px",
                  width: "100%",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: currentValue,
                  }}
                ></div>
              </Typography>
            }
          </Box>
        </Stack>
      </Stack>
    );
  };

  // Helper function to render editable option
  const renderEditableOption = (
    optionNumber: number,
    isEditing: boolean,
    fieldKey: keyof typeof editingStates,
    originalValue: string,
    dispatchAction: any,
    coordinateField: string,
    coordinateDateField: string,
    editedDate?: string
  ) => {
    const optionLabels = ["A", "B", "C", "D"];
    const label = optionLabels[optionNumber - 1];

    // Get the current value from our editedContent state
    const currentValue = editedContent[fieldKey] || originalValue || "";

    return (
      <ListItem disablePadding>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          spacing={"16px"}
          alignItems={"center"}
          height="100%"
          width="100%"
        >
          <ListItemText
            sx={{
              height: "100%",
              display: "flex",
              "& .MuiTypography-root": { width: "100%" },
              width: "100%",
            }}
            primary={
              <Typography
                variant="body2"
                sx={{
                  bgcolor: "#f9fafb",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  padding: "5px 10px",
                  width: "100%",
                  // height:'100%'
                }}
              >{`${label}. ${currentValue}`}</Typography>
            }
          />
          {editedDate && (
            <Stack direction={"row"} alignItems={"center"}>
              <Button
                sx={{ textTransform: "capitalize" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setModalInfo({
                    visible: true,
                    rawText: originalValue,
                  });
                }}
              >{`edited on ${editedDate}`}</Button>
            </Stack>
          )}
          {/* {proofReadingPermissions.includes(
            proofReadingPermission.change_traslation
          ) && isEditing ? (
            <IconButton
              aria-label="edit"
              sx={{ padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();
                toggleEditing(fieldKey, false);
              }}
            >
              <Cancel />
            </IconButton>
          ) : (
            <IconButton
              aria-label="edit"
              sx={{ padding: "0" }}
              onClick={(e) => {
                e.stopPropagation();
                toggleEditing(fieldKey, true);
              }}
            >
              <Edit />
            </IconButton>
          )} */}
        </Stack>
      </ListItem>
    );
  };
  return question ? (
    <Stack height={"100%"} width={"100%"}>
      <Stack
        height="100%"
        boxShadow="rgba(0, 0, 0, 0.2) 0px 3px 3px -2px, rgba(0, 0, 0, 0.14) 0px 3px 4px 0px, rgba(0, 0, 0, 0.12) 0px 1px 8px 0px"
        bgcolor="white"
      >
        {/* Modal for showing original text */}
        <Typography variant="h6" p="15px 0 0 16px" fontWeight={600}>
          {`Question Comparison #${activeQuestionData.questionId}`}
        </Typography>

        {/* <Divider sx={{ margin: "0 16px", width: "calc(100% - 32px)" }} /> */}

        <Stack
          marginTop={"2px"}
          className="scrollBluePrimarythin"
          sx={{ overflowY: "auto" }}
          margin={"10px 0"}
          p="0 16px"
          spacing={"16px"}
          height={"calc(100% - 66.99px)"}
          width={"calc(100% - 32px)"}
        >
          <Stack spacing={"8px"} marginTop={"16px"} direction={"row"}>
            {renderEditableField(
              editingStates.question,
              "question",
              activeQuestionData.questionHindi,
              changeActiveQuestionTranslation,
              activeQuestionData.editedQuestionHindiCoordinate,
              activeQuestionData.editedQuestionHindiCoordinateDate,
              activeQuestionData.editedQuestionHindiDate,
              "Question Text",
              activeQuestionData.question
            )}
          </Stack>

          <Divider />

          {/* Options English */}
          <Stack>
            <Typography
              variant="h6"
              fontSize="0.9rem"
              fontWeight={500}
              color="#374151"
              sx={{
                textDecoration: "underline",
                textUnderlineOffset: "4px",
              }}
            >
              Options
            </Typography>
            <List>
              <Stack
                display="flex"
                direction={"row"}
                justifyContent={"space-between"}
                // alignItems={"center"}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  width="100%"
                  spacing={"16px"}
                >
                  <Box width="calc(50% - 8px)" height="100%" display="flex">
                    <ListItem
                      disablePadding
                      sx={{
                        bgcolor: "#f9fafb",
                        borderRadius: "10px",
                        border: "1px solid #e5e7eb",
                        padding: "5px 10px",
                      }}
                    >
                      <ListItemText
                        sx={{ margin: "0" }}
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ display: "flex", gap: "5px" }}
                          >
                            A.
                            <div
                              dangerouslySetInnerHTML={{
                                __html: activeQuestionData.option1,
                              }}
                            ></div>
                          </Typography>
                        }
                      />
                    </ListItem>
                  </Box>
                  <Box width="calc(50% - 8px)" height="100%" display="flex">
                    {renderEditableOption(
                      1,
                      editingStates.option1,
                      "option1",
                      activeQuestionData.option1Hindi,
                      changeActiveQuestionOption1Translation,
                      activeQuestionData.editedOption1HindiCoordinate,
                      activeQuestionData.editedOption1HindiCoordinateDate,
                      activeQuestionData.editedOption1HindiDate
                    )}
                  </Box>
                </Stack>
              </Stack>{" "}
              <Stack
                display="flex"
                direction={"row"}
                justifyContent={"space-between"}
                // alignItems={"center"}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  width="100%"
                  spacing={"16px"}
                  my="7px"
                >
                  {" "}
                  <Box width="calc(50% - 8px)" height="100%" display="flex">
                    <ListItem
                      disablePadding
                      sx={{
                        bgcolor: "#f9fafb",
                        borderRadius: "10px",
                        border: "1px solid #e5e7eb",
                        padding: "5px 10px",
                      }}
                    >
                      <ListItemText
                        sx={{ margin: "0" }}
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ display: "flex", gap: "5px" }}
                          >
                            {" "}
                            B.
                            <div
                              dangerouslySetInnerHTML={{
                                __html: activeQuestionData.option2,
                              }}
                            ></div>
                          </Typography>
                        }
                      />
                    </ListItem>{" "}
                  </Box>
                  <Box width="calc(50% - 8px)" height="100%" display="flex">
                    {renderEditableOption(
                      2,
                      editingStates.option2,
                      "option2",
                      activeQuestionData.option2Hindi,
                      changeActiveQuestionOption2Translation,
                      activeQuestionData.editedOption2HindiCoordinate,
                      activeQuestionData.editedOption2HindiCoordinateDate,
                      activeQuestionData.editedOption2HindiDate
                    )}
                  </Box>
                </Stack>
              </Stack>
              <Stack
                display="flex"
                direction={"row"}
                justifyContent={"space-between"}
                // alignItems={"center"}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  width="100%"
                  spacing={"16px"}
                  mb="7px"
                >
                  {" "}
                  <Box width="calc(50% - 8px)" height="100%" display="flex">
                    <ListItem
                      disablePadding
                      sx={{
                        bgcolor: "#f9fafb",
                        borderRadius: "10px",
                        border: "1px solid #e5e7eb",
                        padding: "5px 10px",
                      }}
                    >
                      <ListItemText
                        sx={{ margin: "0" }}
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ display: "flex", gap: "5px" }}
                          >
                            {" "}
                            C.
                            <div
                              dangerouslySetInnerHTML={{
                                __html: activeQuestionData.option3,
                              }}
                            ></div>
                          </Typography>
                        }
                      />
                    </ListItem>{" "}
                  </Box>
                  <Box width="calc(50% - 8px)" height="100%" display="flex">
                    {renderEditableOption(
                      3,
                      editingStates.option3,
                      "option3",
                      activeQuestionData.option3Hindi,
                      changeActiveQuestionOption3Translation,
                      activeQuestionData.editedOption3HindiCoordinate,
                      activeQuestionData.editedOption3HindiCoordinateDate,
                      activeQuestionData.editedOption3HindiDate
                    )}{" "}
                  </Box>
                </Stack>
              </Stack>
              <Stack
                display="flex"
                direction={"row"}
                justifyContent={"space-between"}
                // alignItems={"center"}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  width="100%"
                  spacing={"16px"}
                >
                  <Box width="calc(50% - 8px)" height="100%" display="flex">
                    <ListItem
                      disablePadding
                      sx={{
                        bgcolor: "#f9fafb",
                        borderRadius: "10px",
                        border: "1px solid #e5e7eb",
                        padding: "5px 10px",
                      }}
                    >
                      <ListItemText
                        sx={{ margin: "0" }}
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ display: "flex", gap: "5px" }}
                          >
                            {" "}
                            D.
                            <div
                              dangerouslySetInnerHTML={{
                                __html: activeQuestionData.option4,
                              }}
                            ></div>
                          </Typography>
                        }
                      />
                    </ListItem>
                  </Box>{" "}
                  <Box width="calc(50% - 8px)" height="100%" display="flex">
                    {renderEditableOption(
                      4,
                      editingStates.option4,
                      "option4",
                      activeQuestionData.option4Hindi,
                      changeActiveQuestionOption4Translation,
                      activeQuestionData.editedOption4HindiCoordinate,
                      activeQuestionData.editedOption4HindiCoordinateDate,
                      activeQuestionData.editedOption4HindiDate
                    )}
                  </Box>
                </Stack>
              </Stack>
            </List>
          </Stack>

          <Divider />

          {/* Explanation - Editable */}
          {renderEditableField(
            editingStates.explanation,
            "explanation",
            activeQuestionData.explanationHindi,
            changeActiveQuestionExplanationTranslation,
            activeQuestionData.editedExplanationHindiCoordinate,
            activeQuestionData.editedExplanationHindiCoordinateDate,
            activeQuestionData.editedExplanationHindiDate,
            "Explanation",
            activeQuestionData.explanation
          )}
        </Stack>
        {/* Action Buttons */}
      </Stack>
      {/* {questionsDataArray &&
        questionsDataArray.length > 0 &&
        questionsDataArray[questionsDataArray.length - 1].questionId ===
        activeQuestionData.questionId ? (
        <Stack
          my="5px"
          direction={"row"}
          justifyContent={"flex-end"}
          spacing={"8px"}
        >
          <Button
            sx={{ textTransform: "capitalize" }}
            variant="outlined"
            disabled={Object.values(editingStates).some((state) => state)}
            onClick={(e) => {
              e.stopPropagation();
              if (filteredQuestionsDataArray.length > 0) {
                dispatch(
                  goToNextQustion({
                    activeQuestionRowNumber: activeQuestionData.questionId,
                    corrections: null,
                    nextId: filteredQuestionsDataArray[0].questionId,
                  })
                );
              } else {
                setModalInfo({
                  visible: true,
                  rawText: "All the Questions have been reviewed.",
                });
              }
              // handleSaveQuestionUpdates();
            }}
          >
            Next – No Changes
          </Button>
          <Button
            variant="contained"
            disabled={!Object.values(editingStates).some((state) => state)}
            sx={{ textTransform: "capitalize" }}
            onClick={(e) => {
              e.stopPropagation();
              // handleMarkAsDone();
              if (filteredQuestionsDataArray.length > 0) {
                dispatch(
                  showSnack({
                    message: `${filteredQuestionsDataArray.length} Question${filteredQuestionsDataArray.length > 1 ? "s are" : "is"
                      } pending to e Reviewd`,
                    severity: "error",
                    open: true,
                  })
                );
                dispatch(
                  goToNextQustion({
                    activeQuestionRowNumber: activeQuestionData.questionId,
                    corrections: Object.fromEntries(
                      Object.entries(editedContent).filter(
                        ([key]) => editingStates[key]
                      )
                    ),
                    nextId: filteredQuestionsDataArray[0].questionId,
                  })
                );
              } else {
                setModalInfo({
                  visible: true,
                  rawText: "",
                });
              }
            }}
          >
            Submit
          </Button>
        </Stack>
      ) : (
        <Stack
          my="5px"
          direction={"row"}
          justifyContent={"flex-end"}
          spacing={"8px"}
        >
          <Button
            sx={{ textTransform: "capitalize" }}
            variant="outlined"
            disabled={Object.values(editingStates).some((state) => state)}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(
                goToNextQustion({
                  activeQuestionRowNumber: activeQuestionData.questionId,
                  corrections: Object.fromEntries(
                    Object.entries(editedContent).filter(
                      ([key]) => editingStates[key]
                    )
                  ),
                })
              );
              // handleSaveQuestionUpdates();
            }}
          >
            Next – No Changes
          </Button>
          <Button
            variant="contained"
            sx={{ textTransform: "capitalize" }}
            disabled={!Object.values(editingStates).some((state) => state)}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(
                goToNextQustion({
                  activeQuestionRowNumber: activeQuestionData.questionId,
                  corrections: Object.fromEntries(
                    Object.entries(editedContent).filter(
                      ([key]) => editingStates[key]
                    )
                  ),
                })
              );
            }}
          >
            Save Correction & Next
          </Button>
          {filteredQuestionsDataArray.length === 0 ? (
            <Button
              variant="contained"
              // disabled={!Object.values(editingStates).some((state) => state)}
              sx={{ textTransform: "capitalize" }}
              onClick={(e) => {
                e.stopPropagation();
                // handleMarkAsDone();
                if (filteredQuestionsDataArray.length > 0) {
                  dispatch(
                    showSnack({
                      message: `${filteredQuestionsDataArray.length} Question${filteredQuestionsDataArray.length > 1 ? "s are" : "is"
                        } pending to e Reviewd`,
                      severity: "error",
                      open: true,
                    })
                  );
                  dispatch(
                    goToNextQustion({
                      activeQuestionRowNumber: activeQuestionData.questionId,
                      corrections: Object.fromEntries(
                        Object.entries(editedContent).filter(
                          ([key]) => editingStates[key]
                        )
                      ),
                      nextId: filteredQuestionsDataArray[0].questionId,
                    })
                  );
                } else {
                  setModalInfo({
                    visible: true,
                    rawText: "",
                  });
                }
              }}
            >
              Submit
            </Button>
          ) : (
            ""
          )}
        </Stack>
      )} */}
    </Stack>
  ) : (
    <Box
      sx={{
        flex: "8",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        height: "100%",
      }}
    >
      <Typography variant="body1">No Question selected.</Typography>
    </Box>
  );
};
export default AllQuestionPreview;
